import { Flex } from '@chakra-ui/layout';
import { Tag, TagLabel } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import React, { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import {
  SelectInput,
  TextInput,
  WholePageDiffOutline,
} from '../../../../../common/DiffInputs';
import { getBenchmarkFormValues } from './dataMappings/getFormValues';
import { setFormValues } from './dataMappings/setFormValues';
import { EditableTitle } from 'packages/common-frontend/components/InputsBETA/EditableTitle';
import { RelativeRecurringDateForm } from 'packages/common-frontend/components/RecurringDateForm/RelativeRecurringDateForm';
import { usePricingOptionDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/hooks';
import { BenchmarkPricingOptionFormData } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/types';
import { AutoSave } from '~/core/routes/settings/implementations/common/AutoSave';
import { useGetCreditAgreementLazy } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';

export const BenchmarkRateView = ({
  pricingOptionId,
}: {
  pricingOptionId: string;
}) => {
  const getCreditAgreementOrThrow = useGetCreditAgreementLazy();
  const form = useForm<BenchmarkPricingOptionFormData>({
    defaultValues: {
      wipForm: getBenchmarkFormValues(
        getCreditAgreementOrThrow(),
        pricingOptionId,
      ),
    },
    mode: 'onTouched',
  });

  const diffValues = usePricingOptionDiffResult(pricingOptionId);

  useEffect(() => {
    const type = diffValues?.previousPricingOptionFormData?.type;
    if (type === 'benchmark' || type === undefined) {
      form.setValue('diffForm', diffValues?.previousPricingOptionFormData);
    }
  }, [form, diffValues]);

  const changeType = useWatch({
    control: form.control,
    name: 'wipForm.changeType',
  });
  const relativeRecurringDateConfig = useWatch({
    control: form.control,
    name: 'wipForm.relativeRecurringDateConfig',
  });
  const benchmark = useWatch({
    control: form.control,
    name: 'wipForm.benchmark',
  });

  return (
    <FormProvider {...form}>
      <AutoSave<BenchmarkPricingOptionFormData> onSave={setFormValues} />
      <WholePageDiffOutline diff={diffValues?.diffResult}>
        <form>
          <Flex direction="column" gap="1.5rem">
            <Flex gap="1.5rem" direction="column">
              <Flex gap="1.5rem" alignItems="center">
                <EditableTitle<BenchmarkPricingOptionFormData>
                  name="wipForm.name"
                  required
                />
                <Tag variant="solid-muted" colorScheme="blue">
                  <TagLabel>Benchmark: {benchmark}</TagLabel>
                </Tag>
              </Flex>
              <Typography variant="base-muted">
                Margin can be added in Commitments. Select &apos;Review
                Changes&apos; to submit for approval.
              </Typography>
            </Flex>

            <Flex gap="1rem" direction="column">
              <Typography fontWeight="bold">
                Benchmark Rate Retrieval Frequency
              </Typography>
              <Flex
                sx={{
                  border: '1px solid',
                  borderColor: 'gray.200',
                  borderRadius: 'md',
                  flex: 1,
                  flexDirection: 'column',
                  gap: '.5rem',
                  maxW: '100%',
                  p: '1rem',
                }}
              >
                <SelectInput<BenchmarkPricingOptionFormData>
                  name="wipForm.changeType"
                  diffName="diffForm.changeType"
                  label="Change"
                  options={[
                    { label: 'Daily', value: 'daily' },
                    { label: 'Recurring', value: 'recurring' },
                  ]}
                />
                {/* // todo: @markhaghani consider adding to inputs file when done */}
                {changeType === 'recurring' && relativeRecurringDateConfig && (
                  <RelativeRecurringDateForm
                    savedConfig={relativeRecurringDateConfig.dateConfig}
                    setSavedConfig={(newConfigStateAction) => {
                      // Resolving React.SetStateAction<RecurringDate.RelativeConfig> into a value
                      const newConfig =
                        typeof newConfigStateAction === 'function'
                          ? newConfigStateAction(
                              relativeRecurringDateConfig.dateConfig,
                            )
                          : newConfigStateAction;

                      // Object deep equality - to prevent an infinite re-render
                      if (
                        !isEqual(
                          newConfig,
                          relativeRecurringDateConfig.dateConfig,
                        )
                      ) {
                        form.setValue('wipForm.relativeRecurringDateConfig', {
                          dateConfig: {
                            relativeRecurringDateConfig: newConfig.relativeRecurringDateConfig.map(
                              (dateConfig) => ({
                                startDateFormula: dateConfig.startDateFormula, // todo: waiting to change type from base relative date -> relative date. we aren't storing date formulas & will use default formulas - @sachin.
                                endDateFormula: dateConfig.endDateFormula,
                                baseDateConfig: dateConfig.baseDateConfig,
                              }),
                            ),
                            exclusions: newConfig.exclusions, // todo: waiting to add exclusions as part of type change (to base relative date) - @sachinr4
                          },
                        });
                      }
                    }}
                    mode="setup"
                  />
                )}
              </Flex>
            </Flex>

            <Flex direction="column" gap="0.5rem">
              <Typography fontWeight="bold">Base Rate Adjustment</Typography>
              <Flex
                sx={{
                  border: '1px solid',
                  borderColor: 'gray.200',
                  borderRadius: 'md',
                  flex: 1,
                  flexDirection: 'column',
                  gap: '.5rem',
                  maxW: '100%',
                  p: '1rem',
                }}
              >
                <TextInput<BenchmarkPricingOptionFormData>
                  helperText="Optional"
                  label="Ceiling"
                  mask="percentage"
                  name="wipForm.ceiling"
                  diffName="diffForm.ceiling"
                  placeholder="%"
                />
                <TextInput<BenchmarkPricingOptionFormData>
                  helperText="Optional"
                  label="Floor"
                  mask="percentage"
                  name="wipForm.floor"
                  diffName="diffForm.floor"
                  placeholder="%"
                />
                <TextInput<BenchmarkPricingOptionFormData>
                  helperText="Optional. Any additional adjustments to the base rate before it is used."
                  label="Spread"
                  mask="percentage"
                  name="wipForm.spread"
                  diffName="diffForm.spread"
                  placeholder="%"
                />
              </Flex>
            </Flex>

            <TextInput<BenchmarkPricingOptionFormData>
              helperText="Added to the adjusted base rate to calculate the all-in rate."
              label="Margin"
              mask="percentage"
              name="wipForm.margin"
              diffName="diffForm.margin"
              placeholder="%"
              required
            />
          </Flex>
        </form>
      </WholePageDiffOutline>
    </FormProvider>
  );
};
